import cx from 'classnames'
import useClickAway from 'hooks/useClickAway'
import { useRef, useState } from 'react'
import { Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

interface IProps {
  children?: JSX.Element
  header?: string | JSX.Element
  toggler: string | JSX.Element
  className?: string
  onHide?: () => void
  tooltipText?: string | JSX.Element
  title?: string | JSX.Element
}

export const CustomDropdown = ({ children, toggler, header, onHide, className, title, tooltipText }: IProps) => {
  const [visible, setVisible] = useState(false)

  const handleToggleVisible = () => {
    if (visible) onHide && onHide?.()
    setVisible((visible) => !visible)
  }

  const ref = useRef(null)
  useClickAway(ref, () => {
    if (visible) onHide && onHide?.()
    setVisible(false)
  })

  return (
    <Div ref={ref} className={className}>
      {visible ? (
        <div
          className={cx('custom-dropdown-toggle user-select-none', { active: visible })}
          onClick={handleToggleVisible}
          role="button"
        >
          {toggler}
        </div>
      ) : (
        <Tooltip
          className="tenant-selection-tooltip"
          tooltipText={tooltipText!}
          position={PositionEnum.bottom}
          positionContent={100}
          positionTooltipArrow={78}
          tooltipStyles={{
            'min-width': 'auto',
            'max-width': 'fit-content',
            width: 'auto',
            transform: 'translateX(-80%)',
            zIndex: 101,
            background: '#FFF',
            color: '#565B5F',
            hyphens: 'auto',
          }}
        >
          <div
            className={cx('custom-dropdown-toggle user-select-none', { active: visible })}
            onClick={handleToggleVisible}
            role="button"
          >
            {toggler}
          </div>
        </Tooltip>
      )}

      {visible && (
        <div className={cx('custom-dropdown-modal user-select-none', { active: visible })}>
          {title && <div className="custom-dropdown-title">{title}</div>}
          {header && <div className="custom-dropdown-header">{header}</div>}
          <div className="custom-dropdown-content" onClick={handleToggleVisible}>
            {children}
          </div>
        </div>
      )}
    </Div>
  )
}

const Div = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.modal};

  .custom-dropdown-toggle {
    background-color: transparent;
    padding: 0.3rem;
    border-radius: ${({ theme }) => theme.borderRadius.rounded};

    &:hover {
      background-color: ${({ theme }) => theme.colors.primary.lightest};
      color: ${({ theme }) => theme.colors.primary.darker} !important;
    }

    &:focus,
    &:active,
    &.active {
      background-color: ${({ theme }) => theme.colors.grey.lightest};
      color: ${({ theme }) => theme.colors.primary.darker} !important;
    }
  }

  .custom-dropdown-modal {
    min-height: 10vh;
    max-height: 50vh;
    z-index: ${({ theme }) => theme.zIndex.modal};
    padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s} 0 ${({ theme }) => theme.spacing.s};
    overflow-y: auto;
    position: absolute;
    top: 3.125rem;
    right: 0;

    background: ${({ theme }) => theme.colors.grey.lightest};

    border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    box-shadow: ${({ theme }) => theme.shadows.light};
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    color: ${({ theme }) => theme.colors.primary.darker};

    .custom-dropdown-title {
      ${({ theme }) => theme.typography.m.medium};
      margin-bottom: ${({ theme }) => theme.spacing.xxs};
    }

    .custom-dropdown-header {
      color: ${({ theme }) => theme.colors.grey.dark};
      ${({ theme }) => theme.typography.xs.normal};
    }

    .custom-dropdown-content {
      padding-block: 0.675rem;
      ${({ theme }) => theme.typography.s.normal};
    }
  }
`
